import React, { useEffect, useState } from "react";
import { DatasetUploadRow, describeDatasetUpload, formatBytes, listDatasetUploads } from "../../utils/datasetUploadApi";
import { DatasetUploadStatusPill } from "./DatasetUploadStatusPill";

interface DatasetUploadsListProps {
  datasetId: string;
  /** Change it to reload, e.g. after an upload finishes */
  refreshKey?: number | string;
  limit?: number;
}

const formatWhen = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

/**
 * The dataset's upload history as ingest recorded it: every file pushed from a script or this app, with what
 * happened to it. Lets a user who closed the upload modal while a file was processing find its outcome.
 */
const DatasetUploadsList: React.FC<DatasetUploadsListProps> = ({ datasetId, refreshKey, limit = 10 }) => {
  const [uploads, setUploads] = useState<DatasetUploadRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listDatasetUploads(datasetId, { limit })
      .then((page) => {
        if (!cancelled) setUploads(page.uploads);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [datasetId, refreshKey, limit]);

  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <span className="eai-meta-label">Recent uploads</span>
        {loading && <span className="text-eai-body-xs text-eai-ink-subtle">Loading…</span>}
      </div>
      {error && <p className="text-eai-body-sm text-eai-danger-text">{error}</p>}
      {!error && uploads && uploads.length === 0 && (
        <p className="text-eai-body-sm text-eai-ink-subtle">No uploads yet.</p>
      )}
      {!error && uploads && uploads.length > 0 && (
        <ul className="divide-y divide-eai-border rounded-eai-card border border-eai-border">
          {uploads.map((upload) => (
            <li key={upload.id} className="px-3 py-2" data-testid="dataset-upload-row">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <DatasetUploadStatusPill status={upload.status} />
                <span className="truncate text-eai-body-sm font-medium text-eai-ink-muted" title={upload.fileName || upload.id}>
                  {upload.fileName || upload.id}
                </span>
                <span className="ml-auto text-eai-body-xs text-eai-ink-subtle">
                  {formatWhen(upload.processedAt || upload.receivedAt || upload.requestedAt)}
                </span>
              </div>
              <p className="mt-1 text-eai-body-xs text-eai-ink-subtle">
                {[
                  upload.status === "REJECTED" && upload.validationErrors?.length ? null : describeDatasetUpload(upload),
                  upload.sizeBytes ? formatBytes(upload.sizeBytes) : null,
                  upload.uploadedByEmail || null,
                  upload.source === "API" ? "via API" : null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {upload.status === "REJECTED" && upload.validationErrors && upload.validationErrors.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-eai-body-xs text-eai-danger-text">
                  {upload.validationErrors.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              )}
              {upload.status === "LOADED" && upload.warnings && upload.warnings.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-eai-body-xs text-eai-warning-text">
                  {upload.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DatasetUploadsList;
